import React, { useState, useCallback } from 'react';

const SECTORS = ['Real Estate', 'Tech', 'Healthcare', 'Consumer Goods', 'Finance', 'Food & Beverage', 'Fashion', 'Education', 'Other'];
const FUNDING_STAGES = ['Pre-Seed', 'Seed', 'Series A', 'Series B', 'Growth'];
const EMPLOYEE_OPTIONS = ['1-5', '6-10', '11-25', '26-50', '51-100', '100+'];

const ErrorMsg = ({ msg }) => (
  msg ? (
    <div className="flex items-center gap-1 mt-1.5 animate-fadeIn">
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
        <circle cx="8" cy="8" r="7" stroke="#EF4444" strokeWidth="1.5"/>
        <path d="M8 5v3.5M8 11h.01" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      <p className="text-sm" style={{ color: '#EF4444' }}>{msg}</p>
    </div>
  ) : null
);

export default function Step2BusinessDetails({ formData, errors, onChange, onContinue, onBack, onSetErrors }) {
  const [touched, setTouched] = useState({});
  const [shakeBtn, setShakeBtn] = useState(false);
  const descLen = formData?.description?.length || 0;
  const currentYear = new Date()?.getFullYear();

  const validateField = useCallback((name, value) => {
    switch (name) {
      case 'businessName':
        if (!value?.trim()) return 'Please enter your business name';
        if (value?.trim()?.length < 2) return 'Please enter your business name';
        return '';
      case 'websiteUrl':
        if (value?.trim()) {
          try { new URL(value.trim()); } catch { return 'Please enter a valid website URL (e.g. https://yourbusiness.com)'; }
        }
        return '';
      case 'sector':
        if (!value) return 'Please select a business sector';
        return '';
      case 'fundingStage':
        if (!value) return 'Please select your current funding stage';
        return '';
      case 'yearFounded': {
        if (!value?.trim()) return 'Please enter a valid founding year';
        const yr = parseInt(value, 10);
        if (!/^\d{4}$/?.test(value?.trim()) || yr < 1900 || yr > currentYear) return 'Please enter a valid founding year';
        return '';
      }
      case 'employees':
        if (!value) return 'Please select employee range';
        return '';
      case 'description':
        if (!value?.trim()) return 'Please describe your business (minimum 50 characters)';
        if (value?.trim()?.length < 50) return 'Please describe your business (minimum 50 characters)';
        return '';
      default:
        return '';
    }
  }, [currentYear]);

  const handleBlur = (name, value) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    onSetErrors(prev => ({ ...prev, [name]: err || undefined }));
  };

  const handleChange = (name, value) => {
    onChange({ [name]: value });
    if (touched?.[name]) {
      const err = validateField(name, value);
      onSetErrors(prev => ({ ...prev, [name]: err || undefined }));
    }
  };

  const handleContinueClick = () => {
    const fields = ['businessName', 'websiteUrl', 'sector', 'fundingStage', 'yearFounded', 'employees', 'description'];
    const newErrors = {};
    fields?.forEach(f => {
      const err = validateField(f, formData?.[f] || '');
      if (err) newErrors[f] = err;
    });
    setTouched(fields?.reduce((a, f) => ({ ...a, [f]: true }), {}));
    if (Object.keys(newErrors)?.length > 0) {
      onSetErrors(prev => ({ ...prev, ...newErrors }));
      setShakeBtn(true);
      setTimeout(() => setShakeBtn(false), 600);
      const firstErrField = document.querySelector('[data-field-error="true"]');
      if (firstErrField) firstErrField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    onContinue();
  };

  const inputClass = (field) => {
    const hasError = errors?.[field];
    const isValid = touched?.[field] && !hasError && formData?.[field]?.toString()?.trim();
    return `w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 ${
      hasError ? 'border-red-400 bg-red-50' : isValid ? 'border-[#D4A520]' : 'border-gray-200 focus:border-[#D4A520]'
    }`;
  };

  const selectClass = (field) => {
    const hasError = errors?.[field];
    const isValid = touched?.[field] && !hasError && formData?.[field];
    return `w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 appearance-none ${
      hasError ? 'border-red-400 bg-red-50' : isValid ? 'border-[#D4A520]' : 'border-gray-200 focus:border-[#D4A520]'
    }`;
  };

  return (
    <div>
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeInUp 0.2s ease-out forwards; }
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
        .shake-anim { animation: shake 0.5s ease-in-out; }
      `}</style>
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-1" style={{ color: '#0B1F3B', fontFamily: 'Cormorant Garamond, serif' }}>About Your Business</h2>
        <p className="text-sm" style={{ color: '#6B7280' }}>Step 2 of 4 — Business Details</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div data-field-error={!!errors?.businessName}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Business Name <span style={{ color: '#D4A520' }}>*</span></label>
          <input
            type="text"
            value={formData?.businessName}
            onChange={(e) => handleChange('businessName', e?.target?.value)}
            onBlur={(e) => handleBlur('businessName', e?.target?.value)}
            placeholder="Acme Corp"
            className={inputClass('businessName')}
            style={{ color: '#0B1F3B' }}
          />
          <ErrorMsg msg={errors?.businessName} />
        </div>
        <div data-field-error={!!errors?.websiteUrl}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Website URL <span className="text-gray-400 font-normal">(optional)</span></label>
          <input
            type="url"
            value={formData?.websiteUrl}
            onChange={(e) => handleChange('websiteUrl', e?.target?.value)}
            onBlur={(e) => handleBlur('websiteUrl', e?.target?.value)}
            placeholder="https://yourcompany.com"
            className={`w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 ${
              errors?.websiteUrl ? 'border-red-400 bg-red-50' : touched?.websiteUrl && formData?.websiteUrl && !errors?.websiteUrl ? 'border-[#D4A520]' : 'border-gray-200 focus:border-[#D4A520]'
            }`}
            style={{ color: '#0B1F3B' }}
          />
          <ErrorMsg msg={errors?.websiteUrl} />
        </div>
        <div data-field-error={!!errors?.sector}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Business Sector <span style={{ color: '#D4A520' }}>*</span></label>
          <select
            value={formData?.sector}
            onChange={(e) => handleChange('sector', e?.target?.value)}
            onBlur={(e) => handleBlur('sector', e?.target?.value)}
            className={selectClass('sector')}
            style={{ color: formData?.sector ? '#0B1F3B' : '#9CA3AF' }}
          >
            <option value="">Select sector...</option>
            {SECTORS?.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <ErrorMsg msg={errors?.sector} />
        </div>
        <div data-field-error={!!errors?.fundingStage}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Funding Stage <span style={{ color: '#D4A520' }}>*</span></label>
          <select
            value={formData?.fundingStage}
            onChange={(e) => handleChange('fundingStage', e?.target?.value)}
            onBlur={(e) => handleBlur('fundingStage', e?.target?.value)}
            className={selectClass('fundingStage')}
            style={{ color: formData?.fundingStage ? '#0B1F3B' : '#9CA3AF' }}
          >
            <option value="">Select stage...</option>
            {FUNDING_STAGES?.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <ErrorMsg msg={errors?.fundingStage} />
        </div>
        <div data-field-error={!!errors?.yearFounded}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Year Founded <span style={{ color: '#D4A520' }}>*</span></label>
          <input
            type="text"
            value={formData?.yearFounded}
            onChange={(e) => handleChange('yearFounded', e?.target?.value)}
            onBlur={(e) => handleBlur('yearFounded', e?.target?.value)}
            placeholder="2020"
            className={inputClass('yearFounded')}
            style={{ color: '#0B1F3B' }}
            maxLength={4}
          />
          <ErrorMsg msg={errors?.yearFounded} />
        </div>
        <div data-field-error={!!errors?.employees}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Number of Employees <span style={{ color: '#D4A520' }}>*</span></label>
          <select
            value={formData?.employees}
            onChange={(e) => handleChange('employees', e?.target?.value)}
            onBlur={(e) => handleBlur('employees', e?.target?.value)}
            className={selectClass('employees')}
            style={{ color: formData?.employees ? '#0B1F3B' : '#9CA3AF' }}
          >
            <option value="">Select range...</option>
            {EMPLOYEE_OPTIONS?.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <ErrorMsg msg={errors?.employees} />
        </div>
      </div>
      <div className="mb-6" data-field-error={!!errors?.description}>
        <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Business Description <span style={{ color: '#D4A520' }}>*</span></label>
        <div className="relative">
          <textarea
            value={formData?.description}
            onChange={(e) => handleChange('description', e?.target?.value?.slice(0, 250))}
            onBlur={(e) => handleBlur('description', e?.target?.value)}
            placeholder="Describe your business, what problem you solve, and your traction so far..."
            rows={4}
            className={`w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 resize-none ${
              errors?.description ? 'border-red-400 bg-red-50' : touched?.description && !errors?.description && descLen >= 50 ? 'border-[#D4A520]' : 'border-gray-200 focus:border-[#D4A520]'
            }`}
            style={{ color: '#0B1F3B' }}
          />
          <span className={`absolute bottom-3 right-3 text-xs font-medium ${
            descLen > 250 ? 'text-red-500' : descLen >= 50 ? 'text-[#D4A520]' : 'text-gray-400'
          }`}>{descLen}/250</span>
        </div>
        <ErrorMsg msg={errors?.description} />
      </div>
      {/* Required legend */}
      <p className="text-xs mb-4" style={{ color: '#9CA3AF' }}>* Required fields</p>
      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-4 rounded-xl font-bold text-base border-2 transition-all duration-200 hover:bg-gray-50"
          style={{ borderColor: '#0B1F3B', color: '#0B1F3B' }}
        >
          ← Back
        </button>
        <button
          onClick={handleContinueClick}
          className={`flex-1 py-4 rounded-xl font-bold text-base transition-all duration-200 hover:opacity-90 active:scale-[0.98] ${shakeBtn ? 'shake-anim' : ''}`}
          style={{ background: '#D4A520', color: '#0B1F3B' }}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
